import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from "config.json";
import ReactDatePicker from 'react-datepicker';
import './GuestForm.css';
import { es } from 'date-fns/locale';
import useFetch from "hooks/useFetch";

interface User {
  firstname: string;
  lastname: string;
  document: string;
  mail: string;
  id: number;
  phones: Phone[];
  semaphore_status: number;
  observations: string;
  fecha_nacimiento: string;
}

interface Phone {
  phone_number: string;
  active: boolean;
}

interface GuestData {
  fecha_nac: string;
  dni: string;
  apellido_nombre: string;
  condicion: string;
  codigo_habitacion: string;
}

interface RoomData {
  codigo_habitacion: string;
  tipo_habitacion: string;
  cantidad_ocupantes: number;
}

interface ConditionOption {
  value: string;
  label: string;
}

interface Condicion {
  id: number;
  codigo: string;
  descripcion: string;
}

interface FamilyMember {
  id: number;
  dni: string;
  apellidoNombre: string;
  condicion: Condicion;
  fechaNacimiento: string;
  usuarioAsociado: number;
}

interface ApiResponse {
  code: string;
  content: FamilyMember[];
}

const GuestForm: React.FC<{ rooms: RoomData[] }> = ({ rooms }) => {
  const [user, setUser] = useState<User>({} as User);

  const { data: dataUser, error: errorUser, fetchData: fetchDataUser } = useFetch<User>(
    'GET', `${config.backend_url}/authe/token`,
    {},
    { 'Content-Type': 'application/json', 'SetiaAuthorization': `${sessionStorage.getItem('user_token')}` }
  );

  useEffect(() => {
    if (dataUser) {
      setUser(dataUser);
    }
  }, [dataUser]);

  const router = useNavigate();
  const initialGuests: Record<string, GuestData[]> = {};
  rooms.forEach(room => {
    initialGuests[room.codigo_habitacion] = Array(room.cantidad_ocupantes).fill({
      fecha_nac: '',
      dni: '',
      apellido_nombre: '',
      condicion: '',
      codigo_habitacion: room.codigo_habitacion
    });
  });

  const [guests, setGuests] = useState<Record<string, GuestData[]>>(initialGuests);
  const [datosIncompletos, setDatosIncompletos] = useState<string>('');
  const [conditionOptions, setConditionOptions] = useState<ConditionOption[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    if (dataUser && dataUser.document) {
      const claves = Object.keys(initialGuests);
      const primeraClave = claves[0];
      const seteoUsuario = guests[primeraClave].map((guest, index) => {
        if (index === 0) {
          return {
            ...guest,
            dni: dataUser.document,
            fecha_nac: dataUser.fecha_nacimiento,
            apellido_nombre: dataUser.firstname + ' ' + dataUser.lastname,
            condicion: 'AFIL',
            codigo_habitacion: primeraClave
          };
        }
        return guest;
      });

      setGuests(prev => ({
        ...prev,
        [primeraClave]: seteoUsuario
      }));
    }
  }, [dataUser]);

  const handleResize = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    peticionesInciales();
  }, []);

  const peticionesInciales = async () => {
    await fetchDataUser();
    await fetchDataFamily();
  };

  useEffect(() => {
    const url = config.backend_url;

    fetch(url + '/setia/condiciones')
      .then(response => response.json())
      .then(data => {
        const options: ConditionOption[] = data.condiciones
          .filter((condicion: any) =>
            ['AFIL', 'IN-MAY', 'JUBIL'].includes(condicion.codigo))
          .map((condicion: any) => ({
            value: condicion.codigo,
            label: condicion.codigo === 'IN-MAY' ? 'Invitado' : condicion.descripcion
          }));
        setConditionOptions(options);
      })
      .catch(error => console.error('Error al obtener las opciones de condición:', error));
  }, []);

  const handleConditionChange = (codigo_habitacion: string, index: number, value: string) => {
    setGuests(prevGuests => ({
      ...prevGuests,
      [codigo_habitacion]: prevGuests[codigo_habitacion].map((guest, i) => ({
        ...guest,
        condicion: i === index ? value : guest.condicion,
        fecha_nac: i === index ? '' : guest.fecha_nac
      }))
    }));
  };

  const handleGuestChange = (codigo_habitacion: string, index: number, key: keyof GuestData, value: string) => {
    setGuests(prevGuests => ({
      ...prevGuests,
      [codigo_habitacion]: prevGuests[codigo_habitacion].map((guest, i) => ({
        ...guest,
        [key]: i === index ? value : guest[key]
      }))
    }));
  };

  const validateAndUpdateCondition = (guest: GuestData): string => {
    if (!guest.fecha_nac) {
      return guest.condicion;
    }

    const currentDate = new Date();
    const birthDate = new Date(guest.fecha_nac);
    let age = currentDate.getFullYear() - birthDate.getFullYear();
    const m = currentDate.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && currentDate.getDate() < birthDate.getDate())) {
      age--;
    }

    let newCondition = guest.condicion;

    if (age < 12) {
      if (guest.condicion === 'AFIL') {
        newCondition = 'FA-MEN'; // Afiliado de 12 a 18 años
      } else if (guest.condicion === 'IN-MAY') {
        newCondition = 'IN-MEN'; // Invitado de 12 a 18 años
      } else if (guest.condicion === 'PART') {
        newCondition = 'PART'; // Particular de 12 a 18 años
      } else if (guest.condicion === 'JUBIL') {
        newCondition = 'JUBIL'; // Jubilado de 12 a 18 años
      } else {
        newCondition = 'IN-MEN'; // Invitado de 12 a 18 años
      }

    } else if (age >= 12) {
      if (guest.condicion === 'JUBIL') {
        newCondition = 'JUBIL';
      } else if (guest.condicion === 'PART') {
        newCondition = 'PART';
      } else if (guest.condicion === 'IN-MAY') {
        newCondition = 'IN-MAY';
      } else {
        newCondition = 'AFIL'; // Afiliado mayor de 18 años
      }
    }

    return newCondition;

  };

  const handleGuestChangeSelect = (codigo_habitacion: string, index: number, dni: string) => {
    setGuests(prevGuests => ({
      ...prevGuests,
      [codigo_habitacion]: prevGuests[codigo_habitacion].map((guest, i) => {
        if (dni === '') {
          let guest = {
            fecha_nac: '',
            dni: dni,
            apellido_nombre: '',
            condicion: '',
            codigo_habitacion: codigo_habitacion
          };
          return guest;
        }
        if (i === index) {
          // Si selecciona al afiliado, deshabilitamos los campos para este huésped
          let family = getFamilyByDni(dni);
          if (family) {
            let newGuest: GuestData = {
              dni: family.dni,
              fecha_nac: family.fechaNacimiento,
              apellido_nombre: family.apellidoNombre,
              condicion: '',
              codigo_habitacion: codigo_habitacion
            };
            return newGuest;
          }
          return guest;
        } else {
          return guest;
        }
      })
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const incompleteGuest = Object.values(guests).flatMap(guestList =>
      guestList.filter(guest => !guest.fecha_nac || !guest.dni || !guest.apellido_nombre || !guest.condicion || !guest.codigo_habitacion)
    );

    if (incompleteGuest.length > 0) {
      setDatosIncompletos('Por favor completa todos los campos para continuar.');
      return;
    }

    const ocupantes: GuestData[] = Object.values(guests).reduce((accumulator, currentGuests) => {
      const updatedGuests = currentGuests.map(guest => ({
        ...guest,
        condicion: validateAndUpdateCondition(guest)
      }));
      return [...accumulator, ...updatedGuests];
    }, []);

    /*  const afiliadosNoAutorizados: string[] = [];
 
     // Realizar la validación para todos los huéspedes con condición "AFIL"
     for (const ocupante of ocupantes) {
       if (ocupante.condicion === 'AFIL') {
         const dni = ocupante.dni;
         const fechaNac = ocupante.fecha_nac
         console.log(dni, fechaNac);
 
         const [ano, mes, dia] = fechaNac.split('-');
 
       
         try {
           const response = await fetch(`${config.backend_url}/setia/credential?dni=${dni}&fecnac=${ano}-${mes}-${dia}`, {
             headers: {
               "SetiaAuthorization": `${sessionStorage.getItem("user_token")}`,
             }
           });
           const result = await response.json();
 
           // Verificar si el afiliado está autorizado
           if (result.cre === "-1" || result.estado !== "AUTORIZADO A UTILIZAR SERVICIOS SINDICALES") {
             afiliadosNoAutorizados.push(`${ocupante.apellido_nombre} (${ocupante.dni})`);
           }
         } catch (error) {
           afiliadosNoAutorizados.push(`${ocupante.apellido_nombre} (${ocupante.dni}) - Error en la consulta`);
           console.error('Error al verificar afiliado:', error);
         }
       }
     }
 
     // Si hay afiliados no autorizados, mostrar mensaje de error y detener el flujo
     if (afiliadosNoAutorizados.length > 0) {
       setDatosIncompletos(`Los siguientes huéspedes no están habilitados: ${afiliadosNoAutorizados.join(', ')}. Revise la condición ingresada o verifique su habilitación como afiliado.`);
       return;
     }  */

    // Si todos los afiliados están autorizados, continuar
    sessionStorage.setItem('guests_checkout', JSON.stringify(ocupantes));
    console.log('Datos a enviar a la API:', { ocupantes });

    router('/checkout');
  };


  const refs = useRef<HTMLInputElement[][]>([]);

  const handleBacktoRoomsDistribution = (e: React.FormEvent) => {
    e.preventDefault();
    window.location.href = '/rooms-distribution';
  };

  const [familyGroupData, setFamilyGroupData] = useState<FamilyMember[]>([]);
  const { data: dataFamily, isLoading: isLoadingFamily, error: errorFamily, fetchData: fetchDataFamily } = useFetch<ApiResponse>(
    'GET',
    `${config.backend_url}/setia/ocupantes/lista`,
    {},
    {
      'Content-Type': 'application/json',
      'SetiaAuthorization': `${sessionStorage.getItem("user_token")}`
    }
  );

  const obtenerCondicion = (condicion: string) => {
    let resultado = "";
    if (condicion === 'AFIL' || condicion === 'FA-MEN') {
      resultado = 'AFIL';
    }

    if (condicion === 'IN-MEN' || condicion === 'IN-MAY') {
      resultado = 'IN-MAY';
    }

    return resultado;
  };

  const getFamilyByDni = (dni: string) => {
    return familyGroupData.find(member => member.dni === dni);
  };

  function estaElFamiliarEnELGuest(dni: string): boolean {
    let esta = false;

    Object.entries(guests).forEach(([codigoHab, habitaciones]) => {
      for (let i = 0; i < habitaciones.length; i++) {
        if (habitaciones[i].dni === dni && dni !== "") {
          esta = true;
          return esta;
        }
      }
    });
    return esta;
  }

  useEffect(() => {
    if (dataFamily && dataFamily.content) {
      setFamilyGroupData(filterFamilyGroupData(dataFamily.content));
    }
  }, [dataFamily]);

  const filterFamilyGroupData = (familyGroup: FamilyMember[]) => {
    let newFamilyGroup: FamilyMember[] = [];
    let userFamily: FamilyMember = {
      id: user.id,
      dni: user.document,
      apellidoNombre: user.firstname + " " + user.lastname,
      condicion: {
        id: 5,
        codigo: "AFIL",
        descripcion: "Afiliado"
      },
      fechaNacimiento: user.fecha_nacimiento,
      usuarioAsociado: user.id
    };

    newFamilyGroup.push(userFamily);

    familyGroup.forEach((member: FamilyMember) => {
      if (newFamilyGroup.findIndex((m: FamilyMember) => m.dni === member.dni) === -1 && member.condicion.codigo !== "JUBIL") {
        newFamilyGroup.push(member);
      }
    });
    return newFamilyGroup;
  };

  return (
    <div className="listingSection__wrap guest-form">
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">Información de los huéspedes</h2>
      <p>Por favor, complete la información de los huéspedes:</p>
      <p className="font-semibold text-blue-500">Complete la información de todos los huéspedes:</p>
      <form onSubmit={handleSubmit}>
        {rooms.map((room, roomIndex) => (
          <div key={roomIndex}>
            <h2 className="text-blue-600 font-semibold">
              Habitación N°: {room.codigo_habitacion.split("-")[1]} - Categoría: {room.tipo_habitacion}
            </h2>
            <br />
            {guests[room.codigo_habitacion].map((guest, index) => (
              <div key={index}>
                <div className='flex flex-row items-center space-x-2'>
                  <p>Datos del Huésped {index + 1} :</p>
                  <select
                    className="w-full sm:w-1/3"
                    value={guest.dni}
                    onChange={(e) => handleGuestChangeSelect(room.codigo_habitacion, index, e.target.value)}
                  >
                    <option value="">Nuevo huesped</option>
                    {familyGroupData.map((option) => (
                      <option
                        key={option.dni}
                        value={option.dni}
                        style={{ backgroundColor: `${estaElFamiliarEnELGuest(option.dni) ? "#a8a8a8" : "white"}` }}
                        disabled={estaElFamiliarEnELGuest(option.dni)}
                      >
                        {`${option.apellidoNombre}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-wrap sm:flex-nowrap py-3 gap-4">
                  <select
                    className="w-full sm:w-1/4"
                    value={guest.condicion}
                    onChange={(e) => handleConditionChange(room.codigo_habitacion, index, e.target.value)}
                    disabled={guest.dni === user.document}
                  >
                    <option value="">Condición</option>
                    {conditionOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <input
                    type="number"
                    placeholder="DNI"
                    value={guest.dni}
                    onChange={(e) => handleGuestChange(room.codigo_habitacion, index, "dni", e.target.value)}
                    className="w-full sm:w-1/4"
                    disabled={guest.dni === user.document}
                  />

                  <input
                    type="text"
                    placeholder="Apellido y nombre"
                    value={guest.apellido_nombre}
                    onChange={(e) => handleGuestChange(room.codigo_habitacion, index, "apellido_nombre", e.target.value)}
                    className="w-full sm:w-1/4"
                    disabled={guest.dni === user.document}
                  />

                  <div className="w-full sm:w-1/4">
                    {!isMobile ? (
                      <input
                        type="date"
                        lang="es-AR"
                        placeholder="Fecha de nacimiento"
                        value={guest.fecha_nac || ""}
                        onChange={(e) => handleGuestChange(room.codigo_habitacion, index, "fecha_nac", e.target.value)}
                        className="w-full py-2 px-4 border border-gray-200 text-gray-500 rounded-full text-sm"
                        disabled={false}
                      />
                    ) : (
                      <ReactDatePicker
                        selected={guest.fecha_nac ? new Date(guest.fecha_nac) : null}
                        onChange={(date) => {
                          const formattedDate = date ? date.toISOString().split('T')[0] : "";
                          handleGuestChange(room.codigo_habitacion, index, "fecha_nac", formattedDate);
                        }}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Fecha Nac. (DD/MM/AAAA)"
                        className="w-full py-2 px-4 border border-gray-300 rounded-full text-md"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={65}
                        locale={es}
                        calendarClassName="custom-calendar"
                        popperClassName="custom-popper"
                        popperPlacement="bottom-start"
                        disabled={false}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}

        {datosIncompletos && <p className="py-3" style={{ color: "red" }}>{datosIncompletos}</p>}
        <div className="flex justify-between">
          <button className="bg-primary-6000 hover:bg-primary-6000 text-white py-2 px-4 rounded-full" onClick={handleBacktoRoomsDistribution}>
            Volver
          </button>
          <button className="bg-primary-6000 hover:bg-primary-6000 text-white py-2 px-4 rounded-full" onClick={handleSubmit} type="submit">
            Continuar
          </button>
        </div>
      </form>
    </div>
  );
};

export default GuestForm;
